import { IQuestion, IQuestionOption, IPriceCondition, QuestionType, IAssistantConfig } from '../types';

// ============================================================================
// FINAL ACTION QUESTIONS
// ============================================================================

/**
 * Window name collection question
 */
const WINDOW_NAME_QUESTION: IQuestion = {
  questionId: "Q0126_FINAL",
  step: 998,
  category: "999-FINAL-ACTION",
  categoryName: "Final Actions",
  optionType: 0,
  optionTypeName: "",
  question: "Name Your Window",
  questionType: QuestionType.TEXT_INPUT,
  helperText:
    "Give this configuration a memorable name to help you identify it later.",
  options: [
    {
      optionId: "OPTION-WINDOW-NAME",
      optionText: "Window Name",
      description: "",
      responseValue: "window_name",
      optionType: 0,
      inputType: QuestionType.TEXT_INPUT,
      optionTypeName: "",
      order: 0,
      numberOfFrames: 0,
      thumbnail: "",
      price: 0,
      vssPrice: 0,
      priceModifier: 0,
    },
  ],
};

/**
 * Final action question (Add to Cart)
 */
const FINAL_ACTION_QUESTION: IQuestion = {
  questionId: "Q0127_FINAL",
  step: 999,
  category: "999-FINAL-ACTION",
  categoryName: "Final Actions",
  optionType: 0,
  optionTypeName: "",
  question: "What would you like to do next?",
  questionType: QuestionType.SINGLE_SELECT,
  helperText: "Choose an action to continue",
  options: [
    {
      optionId: "A0121",
      optionText: "Add to Cart",
      description: "Add this configuration to your cart",
      responseValue: "addToCart",
      optionType: 0,
      vssPrice: 0,
      inputType: QuestionType.SINGLE_SELECT,
      optionTypeName: "",
      order: 0,
      numberOfFrames: 0,
      thumbnail: "",
      price: 0,
      priceModifier: 0,
    },
  ],
};

/**
 * Final confirmation question (View Cart / Configure Another / Proceed to Quote)
 */
const FINAL_CONFIRMATION_QUESTION: IQuestion = {
  questionId: "Q0128_FINAL",
  step: 1000,
  category: "999-FINAL-ACTION",
  categoryName: "Final Actions",
  optionType: 0,
  optionTypeName: "",
  question: "Would you like to configure another window or view your cart?",
  questionType: QuestionType.SINGLE_SELECT,
  options: [
    {
      optionId: "A0124",
      optionText: "View Cart",
      description: "Review all items in your cart",
      responseValue: "viewCart",
      optionType: 0,
      inputType: QuestionType.SINGLE_SELECT,
      optionTypeName: "",
      order: 0,
      numberOfFrames: 0,
      thumbnail: "",
      price: 0,
      vssPrice: 0,
      priceModifier: 0,
    },
    {
      optionId: "A0122",
      optionText: "Configure Another",
      description: "Start a new window configuration",
      responseValue: "configureAnother",
      optionType: 0,
      inputType: QuestionType.SINGLE_SELECT,
      optionTypeName: "",
      order: 1,
      numberOfFrames: 0,
      thumbnail: "",
      price: 0,
      priceModifier: 0,
      vssPrice: 0,
    },
    {
      optionId: "A0125",
      optionText: "Proceed to Quote",
      description: "Finalize your selections and request a detailed quote",
      responseValue: "proceedToQuote",
      optionType: 0,
      inputType: QuestionType.SINGLE_SELECT,
      optionTypeName: "",
      order: 2,
      numberOfFrames: 0,
      vssPrice: 0,
      thumbnail: "",
      price: 0,
      priceModifier: 0,
    },
  ],
};

// ============================================================================
// TRANSFORMATION FUNCTIONS
// ============================================================================

/**
 * Transform and enrich a question option with pricing logic
 */
function transformOption(
  jsonOption: any,
  allPriceConditions: IPriceCondition[] = []
): IQuestionOption {
  // Filter price conditions that apply to this option
  // Check both trigger_optionids and expected_optionids
  const optionPriceConditions = allPriceConditions.filter((condition: IPriceCondition) => {
    const allTriggers = condition.dogma_trigger_groups?.flat() ?? condition.dogma_trigger_optionids ?? [];
    const allExpected = condition.dogma_expected_groups?.flat() ?? condition.dogma_expected_optionids ?? [];
    const matchesTrigger = allTriggers.some((opt: any) => opt.optionId === jsonOption.optionId);
    const matchesExpected = allExpected.some((opt: any) => opt.optionId === jsonOption.optionId);
    return matchesTrigger || matchesExpected;
  });

  const transformed: IQuestionOption = {
    optionId: jsonOption.optionId || "",
    optionText: jsonOption.optionText || jsonOption.label || "",
    description: jsonOption.description || "",
    responseValue: jsonOption.responseValue || jsonOption.value || "",
    optionType: jsonOption.optionType ?? 0,
    inputType: jsonOption.inputType ?? 5,
    optionTypeName: jsonOption.optionTypeName || "",
    order: jsonOption.order ?? 0,
    numberOfFrames: jsonOption.numberOfFrames ?? 0,
    numberOfSashes: (jsonOption.numberOfFrames ?? 0) * 2,
    optionUnit: jsonOption.optionUnit ?? 1,
    thumbnail: jsonOption.thumbnail || "",
    producttypecode: jsonOption.producttypecode,
    // Pricing fields
    value: jsonOption?.value,
    price: jsonOption.price,
    priceModifier: jsonOption.priceModifier || jsonOption.vssPrice || 0,
    vssPrice: jsonOption.vssPrice ?? 0,
    priceList: jsonOption.priceList,
    priceListDiscounts: jsonOption.priceListDiscounts,
    discountPercentage: jsonOption.discountPercentage,
    discountAmount: jsonOption.discountAmount,
    unglazedDiscountPercentage: jsonOption.unglazedDiscountPercentage,
    unglazedDiscountAmount: jsonOption.unglazedDiscountAmount,
    windowJson: jsonOption.windowJson,
    // Additional fields
    validProducts: jsonOption.validProducts,
    displayCondition: jsonOption.displayCondition,
    priceConditions:
      optionPriceConditions.length > 0 ? optionPriceConditions : undefined,
  };
  return transformed;
}

/**
 * Transform and enrich a question with pricing-aware options
 */
function transformQuestion(
  jsonQuestion: any,
  allPriceConditions: IPriceCondition[] = []
): IQuestion {
  const questionCategory = jsonQuestion.category || '';

  // Transform options with price conditions
  const transformedOptions = jsonQuestion.options?.map((opt: any) =>
    transformOption(opt, allPriceConditions)
  );

  // Collect all unique price conditions from the question's options
  const questionPriceConditions: IPriceCondition[] = [];
  transformedOptions?.forEach((option:IQuestionOption) => {
    if (option.priceConditions) {
      option.priceConditions.forEach(condition => {
        // Add only if not already in the array
        if (!questionPriceConditions.find(pc => pc.dogma_pricingruleid === condition.dogma_pricingruleid)) {
          questionPriceConditions.push(condition);
        }
      });
    }
  });

  const transformed: IQuestion = {
    questionId: jsonQuestion.questionId || '',
    step: jsonQuestion.step ?? 0,
    category: questionCategory,
    categoryName: jsonQuestion.categoryName || '',
    optionType: jsonQuestion.optionType ?? 0,
    optionTypeName: jsonQuestion.optionTypeName || '',
    question: jsonQuestion.question || '',
    questionType: jsonQuestion.questionType ?? 5,
    displayCondition: jsonQuestion.displayCondition,
    responsePath: jsonQuestion.responsePath,
    options: transformedOptions,
    helperText: jsonQuestion.helperText || jsonQuestion.description,
    priceConditions: questionPriceConditions.length > 0 ? questionPriceConditions : undefined,
  };

  return transformed;
}

/**
 * Ensure final action questions are appended to the question flow
 * Links the last question to the window name question and adds all final action questions
 */
function ensureFinalActionQuestion(questions: IQuestion[]): IQuestion[] {
  // Check if final action questions already exist
  const hasFinalAction = questions.some(q => q.category === '999-FINAL-ACTION');
  if (hasFinalAction) {
    return questions;
  }

  // Link the last question to the window name question
  const questionsWithLink = questions.map((q, index) => {
    if (index === questions.length - 1) {
      return { ...q, nextQuestionId: WINDOW_NAME_QUESTION.questionId };
    }
    return q;
  });

  // Append all three final action questions
  return [
    ...questionsWithLink,
    WINDOW_NAME_QUESTION,
    FINAL_ACTION_QUESTION,
    FINAL_CONFIRMATION_QUESTION,
  ];
}

/**
 * Transform config with pricing rules
 * Enriches questions and options with proper pricing information
 * Automatically appends final action questions to the flow
 */
export function transformConfigWithPricing(config: IAssistantConfig): IAssistantConfig {
  const rawRules: IPriceCondition[] = config.pricingRules || [];

  // Merge pricingRuleDiscounts into each matching pricing rule so callers
  // don't need to thread the discount map — the rule carries its own discount.
  const discountMap = new Map(
    (config.pricingRuleDiscounts ?? []).map(d => [d.ruleId, d])
  );
  const allPriceConditions: IPriceCondition[] = rawRules.map(rule => {
    const d = discountMap.get(rule.dogma_pricingruleid);
    if (!d) return rule;
    return { ...rule, discountPercentage: d.discountPercentage, discountAmount: d.discountAmount };
  });

  const transformedQuestions = config.questionFlow?.map((q: any) =>
    transformQuestion(q, allPriceConditions)
  ) || [];

  const questionsWithFinalActions = ensureFinalActionQuestion(transformedQuestions);

  return {
    ...config,
    questionFlow: questionsWithFinalActions,
    chatbotConfig: config.chatbotConfig || {},
    validationRules: config.validationRules || [],
    pricingRules: allPriceConditions.length > 0 ? allPriceConditions : undefined,
  };
}

